import * as React from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import styles from "./Tabs.module.css";
import Box from "@mui/material/Box";
import RadioButton from "../RadioButton/RadioButton";
import SelectComponent from "../Select/Select";
import {
  fluxoObservado,
  velocidade,
  caracteristicasDaVia,
  margemDaVia,
  caracteristicasDaVia2,
  intersecoes,
  instalacoesUVV,
  instalacoesUVV2,
} from "../../environments/labels";
import { useEffect } from "react";
import { useState } from "react";
import { useContext } from "react";
import { ImageView } from "../../contexts/imageView";
import { useSelector } from "react-redux";
import {
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
  Collapse,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import BatchEditModal from "../BatchEdit/BatchEditModal";
import EditAttributesIcon from '@mui/icons-material/EditAttributes';
import { authService } from "../../services/auth.service";

const TAB_SECTIONS = [
  { index: 0, label: "Fluxo observado", data: fluxoObservado },
  { index: 1, label: "Velocidade", data: velocidade },
  { index: 2, label: "Características da via", data: caracteristicasDaVia },
  { index: 3, label: "Características da via 2", data: caracteristicasDaVia2 },
  { index: 4, label: "Atributos da margem da via", data: margemDaVia },
  { index: 5, label: "Interseções", data: intersecoes },
  { index: 6, label: "Instalações UVV", data: instalacoesUVV },
  { index: 7, label: "Instalações UVV 2", data: instalacoesUVV2 },
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export default function BasicTabs(props) {
  const { disabled = false } = props;
  const [value, setValue] = React.useState(0);
  const [segmentos, paginaAtual, validators, hasChanged] = useSelector((state) => [
    state.dadosSegmentos.segmentos,
    state.dadosSegmentos.paginaAtual,
    state.dadosSegmentos.validators,
    state.dadosSegmentos.hasChanged,
  ]);
  const [openBatchEdit, setOpenBatchEdit] = useState(false);
  const [hasLoading, setHasLoading] = useState(false);
  const { imageView, dispatchImageView } = useContext(ImageView);

  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [highlightedField, setHighlightedField] = useState(null);
  const user = authService.getLoggedUser();
  const isAdm = user?.isAdm || false;

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setHighlightedField(null);
  };

  useEffect(() => {
    let loadNewValue = async () => {
      setValue(1);
      setTimeout(() => {
        setValue(0);
      }, "1");
    };
    loadNewValue();
  }, [paginaAtual, hasChanged]);

  const searchResults = React.useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    const term = normalizeSearchText(searchTerm);

    const results = [];
    TAB_SECTIONS.forEach((section) => {
      section.data.forEach((field, fieldIdx) => {
        const title = normalizeSearchText(field.title || "");
        if (title.includes(term)) {
          results.push({
            title: field.title,
            tabIndex: section.index,
            tabLabel: section.label,
            fieldId: `coding-search-field-${section.index}-${fieldIdx}`,
          });
        }
      });
    });
    return results;
  }, [searchTerm]);

  const handleSearchResultClick = (result) => {
    setValue(result.tabIndex);
    setHighlightedField(result.fieldId);
    setTimeout(() => {
      setShowSearch(false);
      setSearchTerm("");
    }, 300);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setHighlightedField(null);
  };

  // Auto-scroll ao campo destacado quando muda de aba
  useEffect(() => {
    if (!highlightedField) return undefined;
    const scrollTimer = setTimeout(() => {
      document.getElementById(highlightedField)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
    return () => clearTimeout(scrollTimer);
  }, [value, highlightedField]);

  // Função para combinar disabled
  const getDisabled = (condition) => {
    return disabled || condition;
  };

  // Wrapper com destaque visual
  const renderSearchableField = (tabIndex, fieldIndex, content) => {
    const fieldId = `coding-search-field-${tabIndex}-${fieldIndex}`;
    const isHighlighted = highlightedField === fieldId;

    return (
      <Box
        key={fieldId}
        id={fieldId}
        sx={{
          borderRadius: "8px",
          backgroundColor: isHighlighted ? "#fff8e1" : "transparent",
          border: isHighlighted ? "2px solid #f09510" : "2px solid transparent",
          padding: isHighlighted ? "8px" : "0px",
          transition: "all 0.3s",
          scrollMarginTop: "120px",
        }}
      >
        {content}
      </Box>
    );
  };

  return (
    <div
      style={{ marginLeft: "12px", marginRight: "12px", marginTop: "0", }}
      className={imageView.hasFlex ? styles.tabsFlewView : ""}
    >
      <Box
        sx={{
          borderRadius: "8px",
          border: "solid 1px grey",
          marginRight: "10px",
        }}
      >
        {/* Botão de Edição em Lote */}
        {isAdm && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 1.5,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditAttributesIcon />}
              onClick={() => setOpenBatchEdit(true)}
              disabled={disabled}
              // sx={{
              //   borderColor: "var(--goia-orange)",
              //   color: "var(--goia-orange)",
              //   "&:hover": {
              //     borderColor: "var(--goia-orange)",
              //     backgroundColor: "var(--goia-orange-100, #fff3e0)",
              //   },
              // }}
              sx={{
                backgroundColor: 'var(--goia-navy)',
                color: 'white',
                '&:hover': { backgroundColor: 'var(--goia-navy-800)' },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              Edição em Lote
            </Button>
          </Box>
        )}
        {/* Barra de busca */}
        <Box sx={{ p: 1.5, borderBottom: "1px solid #e0e0e0" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar campo"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowSearch(true);
              }}
              onFocus={() => setShowSearch(true)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <IconButton onClick={() => setShowSearch(!showSearch)} size="small">
              {showSearch ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={showSearch && searchResults.length > 0}>
            <Paper
              elevation={3}
              sx={{
                mt: 1,
                maxHeight: 250,
                overflow: "auto",
                backgroundColor: "#fafafa",
              }}
            >
              <List dense>
                {searchResults.map((result, idx) => (
                  <ListItem key={idx} disablePadding>
                    <ListItemButton onClick={() => handleSearchResultClick(result)}>
                      <ListItemText
                        primary={result.title}
                        primaryTypographyProps={{ fontSize: "0.9rem" }}
                      />
                      <Chip
                        label={result.tabLabel}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Collapse>

          {showSearch && searchTerm.length >= 2 && searchResults.length === 0 && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "#888", fontStyle: "italic" }}
            >
              Nenhum campo encontrado para "{searchTerm}"
            </Typography>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          {hasLoading ? (
            <Box>loucura loucura</Box>
          ) : (
            <Tabs
              value={value}
              variant="scrollable"
              scrollButtons="auto"
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab label="Fluxo observado" {...a11yProps(0)} />
              <Tab label="Velocidade" {...a11yProps(1)} />
              <Tab label="Características da via" {...a11yProps(2)} />
              <Tab label="Características da via 2" {...a11yProps(3)} />
              <Tab label="Atributos da margem da via" {...a11yProps(4)} />
              <Tab label="Interseções" {...a11yProps(5)} />
              <Tab label="Instalações UVV" {...a11yProps(6)} />
              <Tab label="Instalações UVV2" {...a11yProps(7)} />
            </Tabs>
          )}
        </Box>

        {/* TAB 0 */}
        <TabPanel value={value} index={0}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {fluxoObservado.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });
              if (obj.label.length >= 9) {
                return renderSearchableField(
                  0,
                  i,
                  <SelectComponent
                    key={i}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else if (objeto.title != "id_section") {
                return renderSearchableField(
                  0,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={disabled}
                  />
                );
              }
              return null;
            })}
          </Box>
        </TabPanel>

        {/* TAB 1 */}
        <TabPanel value={value} index={1}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {velocidade.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });
              if (obj.label.length >= 9) {
                return renderSearchableField(
                  1,
                  i,
                  <SelectComponent
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    obj={objeto}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else {
                return renderSearchableField(
                  1,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={disabled || obj.title === "Diferencial de velocidades"}
                  />
                );
              }
            })}
          </Box>
        </TabPanel>

        {/* TAB 2 */}
        <TabPanel value={value} index={2}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {caracteristicasDaVia.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });
              if (obj.label.length >= 9) {
                return renderSearchableField(
                  2,
                  i,
                  <SelectComponent
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    obj={objeto}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else {
                return renderSearchableField(
                  2,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={getDisabled
                      (objeto.title === "Curvatura" ||
                        objeto.title === "Qualidade da curva" &&
                        validators[paginaAtual - 1].curveQuality
                      )}
                  />
                );
              }
            })}
          </Box>
        </TabPanel>

        {/* TAB 3 */}
        <TabPanel value={value} index={3}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {caracteristicasDaVia2.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });
              if (obj.label.length >= 9) {
                return renderSearchableField(
                  3,
                  i,
                  <SelectComponent
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    obj={objeto}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else {
                return renderSearchableField(
                  3,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={getDisabled(objeto.title === "Inclinação")}
                  />
                );
              }
            })}
          </Box>
        </TabPanel>

        {/* TAB 4 - MARGEM DA VIA (mantendo toda a regra de negócio do código 2) */}
        <TabPanel value={value} index={4}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {margemDaVia.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });

              if (obj.label.length >= 9) {
                return renderSearchableField(
                  4,
                  i,
                  <SelectComponent
                    key={`${objeto.title}-${objeto.selecionado?.code ?? "null"}`}
                    selecionado={objeto.selecionado}
                    obj={objeto}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else {
                let fieldDisabled = disabled;
                let disabledOptions = [];

                if (
                  objeto.title ===
                  "Largura do acostamento pavimentado - lado do condutor"
                ) {
                  if (validators[paginaAtual - 1].driverShoulderWidth) {
                    disabledOptions = [1, 2];
                  }
                } else if (
                  objeto.title ===
                  "Largura do acostamento pavimentado - lado do passageiro"
                ) {
                  if (validators[paginaAtual - 1].passengerShoulderWidth) {
                    disabledOptions = [1, 2];
                  }
                } else if (
                  objeto.title === "Sonorizadores ao longo do acostamento"
                ) {
                  fieldDisabled =
                    disabled || !validators[paginaAtual - 1].shoulderSoundEmitters;
                } else if (
                  objeto.title ===
                  "Severidade lateral - distância do lado do condutor"
                ) {
                  if (validators[paginaAtual - 1].driverObjectNone) {
                    disabledOptions = [1, 2, 3];
                  }
                } else if (
                  objeto.title ===
                  "Severidade lateral - distância do lado do passageiro"
                ) {
                  if (validators[paginaAtual - 1].passengerObjectNone) {
                    disabledOptions = [1, 2, 3];
                  }
                }

                return renderSearchableField(
                  4,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={fieldDisabled}
                    disabledOptions={disabledOptions}
                  />
                );
              }
            })}
          </Box>
        </TabPanel>

        {/* TAB 5 */}
        <TabPanel value={value} index={5}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {intersecoes.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });
              if (obj.label.length >= 9) {
                return renderSearchableField(
                  5,
                  i,
                  <SelectComponent
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    obj={objeto}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else {
                const condition =
                  (objeto.title === "Qualidade da intersecção" ||
                    objeto.title === "Canalização da intersecção" ||
                    objeto.title === "Volume de tráfego na via transversal") &&
                  validators[paginaAtual - 1].intersecion;
                return renderSearchableField(
                  5,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={getDisabled(condition)}
                  />
                );
              }
            })}
          </Box>
        </TabPanel>

        {/* TAB 6 */}
        <TabPanel value={value} index={6}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {instalacoesUVV.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });
              if (obj.label.length >= 9) {
                return renderSearchableField(
                  6,
                  i,
                  <SelectComponent
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    obj={objeto}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else {
                const condition =
                  objeto.title === "Qualidade da travessia de pedestres" &&
                  validators[paginaAtual - 1].pedestrianQuality;
                return renderSearchableField(
                  6,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={getDisabled(condition)}
                  />
                );
              }
            })}
          </Box>
        </TabPanel>

        {/* TAB 7 */}
        <TabPanel value={value} index={7}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {instalacoesUVV2.map((obj, i) => {
              let objeto = {};
              Object.keys(segmentos[paginaAtual - 1]).forEach((k) => {
                if (segmentos[paginaAtual - 1][k].title === obj.title) {
                  objeto = segmentos[paginaAtual - 1][k];
                }
              });
              if (obj.label.length >= 9) {
                return renderSearchableField(
                  7,
                  i,
                  <SelectComponent
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    obj={objeto}
                    title={objeto.title}
                    label={obj.label}
                    disabled={disabled}
                  />
                );
              } else {
                const condition =
                  (objeto.title === "Advertência de zona escolar" ||
                    objeto.title === "Supervisor de travessia em zona escolar") &&
                  validators[paginaAtual - 1].educationalZone;
                return renderSearchableField(
                  7,
                  i,
                  <RadioButton
                    key={objeto.title}
                    selecionado={objeto.selecionado}
                    title={objeto.title}
                    label={objeto.label}
                    disabled={getDisabled(condition)}
                  />
                );
              }
            })}
          </Box>
        </TabPanel>
      </Box>
      {isAdm && (
        <BatchEditModal
          open={openBatchEdit}
          onClose={() => setOpenBatchEdit(false)}
        />
      )}
    </div>
  );
}